// MeetScribe API Settings Store Module
import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';

const state = {
  apiKey: null,
  apiKeyVisible: false,
  usage: {
    today: 0,
    total: 0,
    lastUpdated: null
  },
  loading: false,
  error: null,
  // Meetings list for MeetScribe dashboard
  meetings: [],
  meetingsLoading: false,
  meetingsPagination: {
    page: 1,
    total: 0
  },
  // Jobs list for MeetScribe dashboard
  jobs: [],
  jobsLoading: false
};

const getters = {
  getApiKey: (state) => state.apiKey,
  isApiKeyVisible: (state) => state.apiKeyVisible,
  getMaskedApiKey: (state) => {
    if (!state.apiKey) return null;
    if (state.apiKeyVisible) return state.apiKey;
    // Show first 8 and last 4 characters: sk_live_xxxx...xxxx
    const key = state.apiKey;
    if (key.length <= 12) return key;
    return key.substring(0, 8) + '...' + key.substring(key.length - 4);
  },
  getUsage: (state) => state.usage,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  // Meetings getters
  getMeetings: (state) => state.meetings,
  isMeetingsLoading: (state) => state.meetingsLoading,
  getMeetingsPagination: (state) => state.meetingsPagination,
  // Jobs getters
  getJobs: (state) => state.jobs,
  isJobsLoading: (state) => state.jobsLoading
};

const mutations = {
  SET_API_KEY(state, apiKey) {
    state.apiKey = apiKey;
  },
  SET_API_KEY_VISIBLE(state, visible) {
    state.apiKeyVisible = visible;
  },
  SET_USAGE(state, usage) {
    state.usage = {
      today: usage.today || 0,
      total: usage.total || 0,
      lastUpdated: new Date()
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Meetings mutations
  SET_MEETINGS(state, meetings) {
    state.meetings = meetings;
  },
  SET_MEETINGS_LOADING(state, loading) {
    state.meetingsLoading = loading;
  },
  SET_MEETINGS_PAGINATION(state, pagination) {
    state.meetingsPagination = pagination;
  },
  // Jobs mutations
  SET_JOBS(state, jobs) {
    state.jobs = jobs;
  },
  SET_JOBS_LOADING(state, loading) {
    state.jobsLoading = loading;
  }
};

const actions = {
  /**
   * Fetch the current API key for the user
   */
  async fetchApiKey({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await axiosConfig.get(API_URL, {
        params: {
          action: 'get_api_key'
        }
      });

      if (response.data && response.data.success) {
        // Backend returns data.data.api_key (full key)
        if (response.data.data) {
          commit('SET_API_KEY', response.data.data.api_key);
          commit('SET_USAGE', {
            today: response.data.data.requests_today || 0,
            total: response.data.data.requests_total || 0
          });
        } else {
          // No API key exists yet
          commit('SET_API_KEY', null);
        }
      } else {
        throw new Error(response.data?.error || 'Failed to fetch API key');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching API key:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch API key');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Regenerate the API key
   */
  async regenerateApiKey({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = new URLSearchParams();
      params.append('action', 'generate_api_key');

      const response = await axiosConfig.post(API_URL, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data && response.data.success && response.data.data) {
        // Backend returns full api_key only on generation
        commit('SET_API_KEY', response.data.data.api_key);
        commit('SET_API_KEY_VISIBLE', true); // Show the new key
        return response.data;
      } else {
        throw new Error(response.data?.error || 'Failed to regenerate API key');
      }
    } catch (error) {
      console.error('Error regenerating API key:', error);
      commit('SET_ERROR', error.message || 'Failed to regenerate API key');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch API usage statistics
   */
  async fetchApiUsage({ commit }) {
    try {
      const response = await axiosConfig.get(API_URL, {
        params: {
          action: 'get_api_usage'
        }
      });

      if (response.data && response.data.success && response.data.data) {
        // Map backend structure to frontend state
        commit('SET_USAGE', {
          today: response.data.data.daily_stats?.[0]?.requests || 0,
          total: response.data.data.total_requests_all_time || 0
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching API usage:', error);
      throw error;
    }
  },

  /**
   * Toggle API key visibility
   */
  toggleApiKeyVisibility({ commit, state }) {
    commit('SET_API_KEY_VISIBLE', !state.apiKeyVisible);
  },

  /**
   * Clear error state
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  /**
   * Fetch all materials and split into meetings (completed) and jobs (processing)
   * Single API call, data split on client side
   */
  async fetchAllMaterials({ commit }) {
    commit('SET_MEETINGS_LOADING', true);
    commit('SET_JOBS_LOADING', true);

    try {
      const response = await axiosConfig.get(API_URL, {
        params: {
          action: 'get_materials'
        }
      });

      if (response.data && response.data.materials) {
        const allMaterials = response.data.materials;

        // Jobs: pending/processing/transcribing status
        const jobStatuses = ['pending', 'processing', 'transcribing'];
        const jobs = allMaterials
          .filter(m => jobStatuses.includes(m.status))
          .map(m => ({
            id: m.id,
            hash: m.hash,
            title: m.caption || m.title || m.video_title || 'Untitled',
            status: m.status,
            statusComment: m.status_comment,
            updatedAt: m.updated_at
          }));

        // Meetings: completed materials only (not in jobs)
        const meetings = allMaterials
          .filter(m => !jobStatuses.includes(m.status))
          .map(m => ({
            id: m.id,
            hash: m.hash,
            title: m.caption || m.title || m.video_title || 'Untitled Meeting',
            status: m.status,
            link: `/article/${m.hash}`,
            updatedAt: m.updated_at,
            duration: m.duration
          }));

        commit('SET_MEETINGS', meetings);
        commit('SET_MEETINGS_PAGINATION', {
          page: 1,
          total: meetings.length
        });
        commit('SET_JOBS', jobs);
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching materials:', error);
      throw error;
    } finally {
      commit('SET_MEETINGS_LOADING', false);
      commit('SET_JOBS_LOADING', false);
    }
  },

  /**
   * Refresh meetings only (calls fetchAllMaterials)
   */
  async fetchMeetings({ dispatch }) {
    return dispatch('fetchAllMaterials');
  },

  /**
   * Refresh jobs only (calls fetchAllMaterials)
   */
  async fetchJobs({ dispatch }) {
    return dispatch('fetchAllMaterials');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
